import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Image } from "react-bootstrap";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Button from "react-bootstrap/Button";
import { useReactiveVar } from "@apollo/client";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import {
    TableHeader,
    Pagination,
    ReactSelect,
    FiltersButton,
    ProgressBar,
    BadgeAndButtonForArchive,
    RenderEmptyList,
    Spinner,
    InfoBanner,
    WithRoleAccess,
} from "@services/ui-components";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ModalCloneMenuItem from "./modals/ModalCloneMenuItem";
import ModalDeleteMenuItem from "./modals/ModalDeleteMenuItem";
import { getSteps, IStep } from "./constants";
import emptyImage from "../../../images/dishes.png";
import { ApolloError } from "@apollo/client";
import { ErrorModalComponent } from "@me-pos/error-modal";
import i18next from "i18next";
import { CompanyAccess, MenuItemProxy } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import { useGetMenuItemsQuery, useGetMenuTypesQuery } from "@me-team/host/main/menu/graphql/menu.hooks";
import { MenuModalState } from "../../../interfaces/meMenuInterfaces/interfaces";
import { initialMenuModalState } from "../AllMenusPage/constants";
import { createMenuModalHelpers } from "../AllMenusPage/helpers";
import { useCloseTooltipsOnModalOpen } from "../../../hooks/useCloseTooltipsOnModalOpen";
import { Option, OptionString } from "../../../interfaces/interfaces";
import { getLanguageVersionOptions, getMenuTypeEnumOptions } from "../AllMenusPage/options";
import ErrorService from "../../../services/ErrorService";
import { cloneNameToDisplay } from "../utils/cloneNameUtils";
import AddEditMenuModal from "../AllMenusPage/modals/AddEditMenuModal/AddEditMenuModal";
import ToastComponent from "../../ui-components/ToastComponent/ToastComponent";
import { useToast } from "../../../hooks/useToast";
import {
    useGetCategoriesForMenuItemsFilterQuery,
    useDeleteMenuItemMutation,
} from "@me-team/host/main/menuItems/graphql/menuItems.hooks";
import emptyImgDish from "../../../images/empty-img-dish.png";
import { menuLanguageVersionVar } from "@me-team/host/main/menuLanguageVersions/state";
import MenuLanguageVersionSelectContainer from "../components/LanguageVersionSelect/MenuLanguageVersionSelectContainer";
import { useMenuLanguagesData } from "../utils/hooks/useMenuLanguagesData";
import { buildTranslationTextVm } from "../utils/translationVersions/buildTranslationTextVm";
import TranslationMissingDot from "../components/LanguageVersionSelect/TranslationMissingDot";
import { buildPrimaryLanguageVm, buildSelectedLanguageVm } from "../SupplementsForDishesPage/utils/languageVm";
import {
    createEmptyCategorySelection,
    MenuCategorySelection,
    resolveMenuCategoryId,
} from "./helper/menuCategoryFilterHelper";
import MenuCategoryFilterPicker from "./MenuCategoryFilterPicker";

const basePath = process.env.REACT_APP_POS_URL;

interface MenuItemsFilterState {
    search: string;
    category: MenuCategorySelection;
}

const MenuItemsPage: React.FC = () => {
    const { t } = useTranslation();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canCreateMenu = useRoleAccess(CompanyAccess.MenuCreate);
    const canCreateDish = useRoleAccess(CompanyAccess.MenuDishCreate);
    const canCloneDish = useRoleAccess(CompanyAccess.MenuDishClone);
    const canEditDish = useRoleAccess(CompanyAccess.MenuDishEdit);
    const canDeleteRestoreDish = useRoleAccess(CompanyAccess.MenuDishDeleteRestore);
    const selectedLangVersionCode = useReactiveVar(menuLanguageVersionVar);
    const navigate = useNavigate();
    const steps: IStep[] = getSteps(t);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState<number>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(20);
    const [showFilters, setShowFilters] = useState(false);
    const { showToast, toastText, setShowToast, toggleShowToast } = useToast();

    const [searchParams] = useSearchParams();
    const categoryIdFromUrl = Number(searchParams.get("categoryId")) || null;

    const toggleFilters = () => setShowFilters(!showFilters);
    const [filter, setFilter] = useState<MenuItemsFilterState>({
        search: "",
        category: createEmptyCategorySelection(),
    });
    const [menuModalState, setMenuModalState] = useState<MenuModalState>(initialMenuModalState);
    const { openCreateMenuModal, closeMenuModal } = useMemo(() => createMenuModalHelpers(setMenuModalState), []);

    const { tooltipResetKey, resetTooltips } = useCloseTooltipsOnModalOpen();
    const { data: menuTypesData, loading: menuTypesLoading } = useGetMenuTypesQuery({
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            console.error("error", error);
        },
    });
    const menuTypeOptions: OptionString[] = getMenuTypeEnumOptions(menuTypesData?.menuTypes, (name) => t(name));

    const { primaryLanguage, languages } = useMenuLanguagesData(Number(currentCompanyId));

    const primaryLanguageIsoCode = primaryLanguage?.isoCode?.trim() || null;

    const isTranslationMode = Boolean(
        selectedLangVersionCode && primaryLanguageIsoCode && selectedLangVersionCode !== primaryLanguageIsoCode
    );

    const languageVersionOptions: Option[] = languages && getLanguageVersionOptions(languages);

    const primaryLanguageVm = useMemo(
        () => buildPrimaryLanguageVm(primaryLanguage, languages),
        [primaryLanguage, languages]
    );
    const selectedLanguageVm = useMemo(
        () => buildSelectedLanguageVm(selectedLangVersionCode, primaryLanguageVm, languages),
        [selectedLangVersionCode, primaryLanguageVm, languages]
    );

    const [currentFilter, setCurrentFilter] = useState<MenuItemsFilterState>({
        search: "",
        category: createEmptyCategorySelection(),
    });
    const [sortBy, setSortBy] = useState("name");
    const [order, setOrder] = useState("ASC");
    const appliedMenuCategoryId = resolveMenuCategoryId(filter.category);

    const {
        data: menuItemsData,
        loading,
        refetch: refetchMenuItems,
    } = useGetMenuItemsQuery({
        variables: {
            companyId: +currentCompanyId,
            itemsPerPage,
            page: currentPage,
            sortBy,
            order,
            search: filter.search ? filter.search : null,
            menuCategoryId: categoryIdFromUrl || appliedMenuCategoryId,
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
        context: {
            errorType: "local",
        },
        onError: (error: ApolloError) => {
            setIsErrorModalOpen(ErrorService.errorHandling(error));
        },
        fetchPolicy: "network-only",
    });

    const currency = menuItemsData?.user?.company?.currency?.currencySign;

    const [showCloneModal, setShowCloneModal] = useState<boolean>(false);
    const [currentDishId, setCurrentDishId] = useState<number>(null);

    const { data: categoriesData, loading: categoriesLoading } = useGetCategoriesForMenuItemsFilterQuery({
        variables: {
            companyId: Number(currentCompanyId),
            locale: selectedLangVersionCode,
        },
        skip: !currentCompanyId || !selectedLangVersionCode,
    });

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<number>(null);
    const [deleteModalMessage, setDeleteModalMessage] = useState("");
    const [itemToDeleteName, setItemToDeleteName] = useState(null);
    const [itemToDeleteNameOrigin, setItemToDeleteNameOrigin] = useState<string | null>(null);
    const [deleteMenuItemMutation] = useDeleteMenuItemMutation();

    const handleCloneClick = (id: number) => {
        if (!canCloneDish) return;
        setCurrentDishId(id);
        setShowCloneModal(true);
    };

    const handleCloseCloneMenuItem = () => {
        resetTooltips();
        setShowCloneModal(false);
    };

    const handleDeleteClick = (menuItem: any) => {
        if (!canDeleteRestoreDish) return;
        setItemToDelete(menuItem?.id);
        setItemToDeleteName(menuItem?.name);
        setItemToDeleteNameOrigin(menuItem?.nameOrigin ?? null);
        const isUsedInMenu = menuItem?.isUsedInMenu;

        if (isUsedInMenu) {
            setDeleteModalMessage(
                t(
                    "Are you sure you want to delete this dish? The dish has links in the menu. It will be moved to the archive, and you will be able to restore it."
                )
            );
        } else {
            setDeleteModalMessage(
                t(
                    "Are you sure you want to delete this dish? It will be moved to the archive, and you will be able to restore it."
                )
            );
        }

        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!canDeleteRestoreDish) return;
        if (!itemToDelete || !currentCompanyId) return;

        try {
            await deleteMenuItemMutation({
                variables: {
                    id: itemToDelete,
                    branchId: Number(branchId),
                },
                onCompleted: () => {
                    toggleShowToast(t("Deleted"));
                },
            });

            await refetchMenuItems();
            setShowDeleteModal(false);
            setItemToDelete(null);
        } catch (error) {
            console.error(error);
        }
    };

    const handleApplyFilter = () => {
        setFilter(currentFilter);
        setCurrentPage(1);
    };

    const handleClearFilter = () => {
        setFilter({ search: "", category: createEmptyCategorySelection() });
        setCurrentFilter({ search: "", category: createEmptyCategorySelection() });
        setSortBy("name");
        setOrder("ASC");
        setCurrentPage(1);
    };

    const handlePageChange = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    useEffect(() => {
        if (!selectedLangVersionCode) return;
        setCurrentPage(1);
    }, [selectedLangVersionCode]);

    const menuCategories = categoriesData?.user?.company?.meMenuCategories;

    const handleEditMenuItem = (id: number) => {
        if (!canEditDish) return;
        navigate(`/menu/update-dish/dish/${id}/branch/${branchId}`);
    };

    const menuItems =
        menuItemsData?.user?.company?.menuItems?.items?.map((item: MenuItemProxy) => ({
            id: item.id,
            name: item.name,
            nameOrigin: item.nameOrigin,
            categoriesData: item.categories,
            categories: item.categories
                .map((category) => {
                    const subcategories = category?.childCategories?.map((sub) => sub.name).join(", ");
                    return `${category.name}${subcategories ? "/" + subcategories : ""}`;
                })
                .join(", "),
            isUsedInMenu: Boolean(item.menus?.length),
            price: `${item.portions.map((portion) => portion.price).join("/")} ${currency}`,
            volume: `${item.portions.map((portion) => portion.volume).join("/")} ${item.portions[0].item.unit.name}`,
            relatedMenuCount: item?.menus?.length,
            isArchive: item.deleted,
            image: item.favicon ? `${basePath}/${item.favicon}` : emptyImgDish,
        })) || [];
    const pageCount = parseInt(menuItemsData?.user?.company?.menuItems?.pageCount || "0");
    const totalItems = menuItemsData?.user?.company?.menuItems?.totalCount || 0;
    const firstItemIndex = (currentPage - 1) * itemsPerPage + 1;
    const lastItemIndex = Math.min(currentPage * itemsPerPage, totalItems);

    const sortingOptions = [
        { value: { sortBy: "name", order: "ASC" }, label: t("Alphabetically (A-Z)") },
        { value: { sortBy: "name", order: "DESC" }, label: t("Alphabetically (Z-A)") },
        { value: { sortBy: "price", order: "ASC" }, label: t("From cheapest") },
        { value: { sortBy: "price", order: "DESC" }, label: t("From most expensive") },
    ];

    const isFilterNotEmpty = !!(filter.search || appliedMenuCategoryId);

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleApplyFilter();
        }
    };

    if (menuTypesLoading) return <Spinner />;

    return (
        <div className="mb-4">
            <InfoBanner>
                <p className="fs-7 mb-0">
                    {t(
                        "This page displays a list of all dishes created in the company's various menus. You can view, add, edit, or delete dishes."
                    )}
                </p>
            </InfoBanner>
            <TableHeader
                title={t("List of dishes")}
                paths={[
                    { path: `/branches/${branchId}`, label: "Branches" },
                    { path: `/menu/dishes/branch/${branchId}`, label: t("List of dishes") },
                ]}
                buttons={
                    <Row className="gx-3 justify-content-end mt-3 mt-lg-0">
                        <Col md={6} xl={3} className="mb-3 mb-xxl-0" style={{ minWidth: "220px" }}>
                            <div className="position-relative">
                                <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                    {t("Language version")}
                                </span>
                                <MenuLanguageVersionSelectContainer companyId={Number(currentCompanyId)} />
                            </div>
                        </Col>
                        <WithRoleAccess permission={CompanyAccess.MenuCreate}>
                            <Col md={6} xl={4} xxl={3} className="mb-3 mb-xxl-0">
                                <Button
                                    className="btn btn-linear-gradient border-0 fw-bold py-6 text-capitalize w-100 text-nowrap"
                                    onClick={() => {
                                        if (canCreateMenu) openCreateMenuModal();
                                    }}
                                >
                                    <i className="bi bi-book me-2"></i>
                                    {t("Create menu")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <WithRoleAccess permission={CompanyAccess.MenuDishCreate}>
                            <Col xs={9} xl={3}>
                                <Button
                                    onClick={() => {
                                        if (canCreateDish) navigate(`/menu/dishes/create-dish/branch/${branchId}`);
                                    }}
                                    className="btn btn-primary fw-bold text-capitalize text-truncate w-100"
                                >
                                    <i className="bi bi-plus-lg me-lg-2"></i> {t("Add dish")}
                                </Button>
                            </Col>
                        </WithRoleAccess>
                        <Col xs={3} xl={2}>
                            <FiltersButton
                                filter={{
                                    search: filter.search,
                                    menuCategoryId: appliedMenuCategoryId,
                                }}
                                showFilters={showFilters}
                                onClick={toggleFilters}
                                classesText="d-none d-xxl-inline-block"
                            />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
                colSize={4}
                btnColSize={8}
            >
                <Row className="gx-3">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col lg={4}>
                                <div className="position-relative mb-3 mb-lg-0">
                                    <input
                                        id="listOfDishes-search-field"
                                        type="text"
                                        className="form-control rounded-1 ps-6 pe-5 text-truncate"
                                        placeholder={t("Search by name, description")}
                                        value={currentFilter.search}
                                        onChange={(e) => setCurrentFilter({ ...currentFilter, search: e.target.value })}
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                    <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-3"></i>
                                </div>
                            </Col>
                            <Col lg={4} className="mb-3 mb-lg-0">
                                <MenuCategoryFilterPicker
                                    inputId="listOfDishes-category-field"
                                    categories={menuCategories}
                                    value={currentFilter.category}
                                    placeholder={t("Category")}
                                    onChange={(category) =>
                                        setCurrentFilter((prevState) => ({
                                            ...prevState,
                                            category,
                                        }))
                                    }
                                    disabled={categoriesLoading || !selectedLangVersionCode}
                                    isTranslationMode={isTranslationMode}
                                    translationTooltip={t("Edit the language version")}
                                />
                            </Col>
                            <Col lg={4} className="mb-3 mb-lg-0">
                                <div className="position-relative h-100">
                                    <span className="position-absolute start-0 top-0 z-1 ps-6 pt-1 fs-8 text-grey">
                                        {t("Sort")}
                                    </span>
                                    <ReactSelect
                                        id="listOfDishes-sort-field"
                                        value={sortingOptions.find(
                                            (option) => option.value.sortBy === sortBy && option.value.order === order
                                        )}
                                        options={sortingOptions}
                                        placeholder={t("Sort by")}
                                        onChange={(selectedOption) => {
                                            setSortBy(selectedOption.value.sortBy);
                                            setOrder(selectedOption.value.order);
                                        }}
                                        isSearchable={false}
                                        isTitle={true}
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                </div>
                            </Col>
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6}>
                                <Button
                                    variant="outline-dark"
                                    className="border-grey w-100 h-100 fw-normal"
                                    onClick={handleClearFilter}
                                >
                                    {t("clear")}
                                </Button>
                            </Col>
                            <Col xs={6}>
                                <Button
                                    variant="primary"
                                    className="btn btn-primary w-100 h-100 fw-normal px-1"
                                    onClick={handleApplyFilter}
                                >
                                    {t("apply")}
                                </Button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>

            <ProgressBar currentStep={2} steps={steps} marginBottom="mb-4" isMenu />
            <div className="table-responsive mb-4">
                {loading || !selectedLangVersionCode ? (
                    <Spinner />
                ) : menuItems.length > 0 ? (
                    <table className="table mb-2">
                        <thead>
                            <tr className="fs-7">
                                <th className="col-1 text-center px-4">{t("photo")}</th>
                                <th>{t("name")}</th>
                                <th>{t("categoriesSubcategories")}</th>
                                <th>{t("pricePrices")}</th>
                                <th>{t("volume")}</th>
                                <th>{t("menu")}</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {menuItems.map((menuItem: any) => {
                                const colorForArchive = menuItem?.isArchive ? "text-secondary" : "";
                                const nameVm = buildTranslationTextVm({
                                    translatedValue: menuItem.name,
                                    originValue: menuItem.nameOrigin,
                                    isTranslationMode,
                                });
                                const categoryNodes = (menuItem.categoriesData ?? []).map((category) => {
                                    const categoryNameVm = buildTranslationTextVm({
                                        translatedValue: category.name,
                                        originValue: category.nameOrigin,
                                        isTranslationMode,
                                    });
                                    const subcategoryNodes = (category.childCategories ?? []).map((sub) => {
                                        const subNameVm = buildTranslationTextVm({
                                            translatedValue: sub.name,
                                            originValue: sub.nameOrigin,
                                            isTranslationMode,
                                        });

                                        return (
                                            <span key={sub.id} className="d-inline-flex align-items-center">
                                                <span className="me-1">{subNameVm.displayText}</span>
                                                {subNameVm.isMissingTranslation ? (
                                                    <TranslationMissingDot
                                                        tooltipText={t("Edit the language version")}
                                                    />
                                                ) : null}
                                            </span>
                                        );
                                    });

                                    return (
                                        <span key={category.id} className="d-inline-flex align-items-center flex-wrap">
                                            <span className="me-1">{categoryNameVm.displayText}</span>
                                            {categoryNameVm.isMissingTranslation ? (
                                                <TranslationMissingDot tooltipText={t("Edit the language version")} />
                                            ) : null}
                                            {subcategoryNodes.length ? (
                                                <>
                                                    <span className="mx-1">/</span>
                                                    {subcategoryNodes.map((node, index) => (
                                                        <React.Fragment key={index}>
                                                            {node}
                                                            {index < subcategoryNodes.length - 1 ? (
                                                                <span>, </span>
                                                            ) : null}
                                                        </React.Fragment>
                                                    ))}
                                                </>
                                            ) : null}
                                        </span>
                                    );
                                });
                                const displayedCategories = categoryNodes.slice(0, 2);
                                const hiddenCategories = categoryNodes.slice(2);
                                const renderCategoryNodes = (nodes: React.ReactNode[]) =>
                                    nodes.map((node, index) => (
                                        <React.Fragment key={index}>
                                            {node}
                                            {index < nodes.length - 1 ? <span>; </span> : null}
                                        </React.Fragment>
                                    ));

                                return (
                                    <tr key={menuItem.id} className="align-middle">
                                        <td className="align-middle">
                                            <div
                                                className="d-flex justify-content-center align-items-center rounded-circle border border-secondary mx-auto img-size"
                                                style={{ opacity: menuItem?.isArchive ? "0.4" : "" }}
                                            >
                                                <Image
                                                    className="rounded-circle"
                                                    src={menuItem?.image}
                                                    width="40px"
                                                    height="40px"
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <span className={colorForArchive}>
                                                {cloneNameToDisplay(nameVm.displayText, t)}
                                            </span>
                                            {nameVm.isMissingTranslation ? (
                                                <TranslationMissingDot tooltipText={t("Edit the language version")} />
                                            ) : null}
                                        </td>
                                        <td className={colorForArchive}>
                                            {hiddenCategories.length > 0 ? (
                                                <>
                                                    {renderCategoryNodes(displayedCategories)};
                                                    <OverlayTrigger
                                                        placement="bottom"
                                                        overlay={
                                                            <Tooltip>
                                                                <div>{renderCategoryNodes(hiddenCategories)}</div>
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span className="cursor-pointer"> ...</span>
                                                    </OverlayTrigger>
                                                </>
                                            ) : (
                                                <span>{renderCategoryNodes(displayedCategories)}</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`${colorForArchive} text-nowrap`}>{menuItem?.price}</span>
                                        </td>
                                        <td>
                                            <span className={`${colorForArchive} text-nowrap`}>{menuItem.volume}</span>
                                        </td>
                                        <td>
                                            <span className={colorForArchive}>{menuItem?.relatedMenuCount}</span>
                                        </td>
                                        <td>
                                            <div className="d-flex gap-2 justify-content-end align-items-center flex-nowrap">
                                                {!menuItem?.isArchive ? (
                                                    <>
                                                        <WithRoleAccess permission={CompanyAccess.MenuDishClone}>
                                                            <OverlayTrigger
                                                                key={tooltipResetKey}
                                                                overlay={<Tooltip>{t("Clone")}</Tooltip>}
                                                            >
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() => handleCloneClick(menuItem?.id)}
                                                                    className="py-2 text-dark rounded-1"
                                                                >
                                                                    <i className="bi bi-stickies"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </WithRoleAccess>
                                                        <WithRoleAccess permission={CompanyAccess.MenuDishEdit}>
                                                            <OverlayTrigger overlay={<Tooltip>{t("Edit")}</Tooltip>}>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    onClick={() => handleEditMenuItem(menuItem?.id)}
                                                                    className="p-2 fs-7 text-dark rounded-1"
                                                                >
                                                                    <i className="bi bi-pencil px-1"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </WithRoleAccess>
                                                        <WithRoleAccess
                                                            permission={CompanyAccess.MenuDishDeleteRestore}
                                                        >
                                                            <OverlayTrigger overlay={<Tooltip>{t("Delete")}</Tooltip>}>
                                                                <Button
                                                                    variant="outline-secondary"
                                                                    className="py-2 text-dark rounded-1"
                                                                    onClick={() => handleDeleteClick(menuItem)}
                                                                >
                                                                    <i className="bi bi-trash3"></i>
                                                                </Button>
                                                            </OverlayTrigger>
                                                        </WithRoleAccess>
                                                    </>
                                                ) : (
                                                    <BadgeAndButtonForArchive
                                                        hideButton={!canDeleteRestoreDish}
                                                        onButtonClick={() => {
                                                            if (canDeleteRestoreDish) {
                                                                navigate(
                                                                    `/menu/restore-dish/dish/${menuItem.id}/branch/${branchId}`
                                                                );
                                                            }
                                                        }}
                                                    />
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                ) : (
                    <RenderEmptyList
                        isFilterNotEmpty={isFilterNotEmpty}
                        imageSrc={emptyImage}
                        altText="Dishes"
                        emptyFilterTitle="No data found for the given values"
                        emptyListTitle="You don't have any dishes yet. Create your first dish"
                        buttonName={
                            canCreateDish ? (
                                <span className="fw-bold">
                                    <i className="bi bi-plus-lg fw-bold me-1 w-100"></i>
                                    {t("Create dish")}
                                </span>
                            ) : null
                        }
                        onClickButton={() => {
                            if (canCreateDish) navigate(`/menu/dishes/create-dish/branch/${branchId}`);
                        }}
                    />
                )}
            </div>

            {menuItems.length > 0 ? (
                <div className="d-flex justify-content-between align-items-center">
                    <Pagination
                        t={t}
                        pages={Number(pageCount)}
                        currentPage={currentPage}
                        onPageChange={handlePageChange}
                        firstItemIndex={firstItemIndex}
                        lastItemIndex={lastItemIndex}
                        totalItems={totalItems}
                    />
                </div>
            ) : null}

            {!!selectedLangVersionCode ? (
                <ModalCloneMenuItem
                    showCloneModal={showCloneModal}
                    onCloseModal={handleCloseCloneMenuItem}
                    setShowCloneModal={setShowCloneModal}
                    currentDishId={currentDishId}
                    refetchMenuItems={refetchMenuItems}
                    localeVersion={selectedLangVersionCode}
                    headerLanguage={selectedLanguageVm?.label}
                    canCloneDish={canCloneDish}
                    isTranslationMode={isTranslationMode}
                />
            ) : null}
            <ModalDeleteMenuItem
                showDeleteModal={showDeleteModal}
                setShowDeleteModal={setShowDeleteModal}
                deleteModalMessage={deleteModalMessage}
                handleConfirmDelete={handleConfirmDelete}
                itemToDeleteName={itemToDeleteName}
                itemToDeleteNameOrigin={itemToDeleteNameOrigin}
                isTranslationMode={isTranslationMode}
                canDeleteRestoreDish={canDeleteRestoreDish}
            />

            {menuModalState.isOpen && primaryLanguage && languageVersionOptions.length > 0 && (
                <AddEditMenuModal
                    show={menuModalState.isOpen}
                    mode={menuModalState.mode}
                    onClose={closeMenuModal}
                    languageVersionOptions={languageVersionOptions}
                    menuTypeOptions={menuTypeOptions}
                    primaryLanguage={primaryLanguage}
                />
            )}

            {isErrorModalOpen ? (
                <ErrorModalComponent
                    i18n={i18next}
                    onClose={() => {
                        setIsErrorModalOpen(null);
                    }}
                    isOpen={!!isErrorModalOpen}
                    currentError={isErrorModalOpen}
                />
            ) : null}
            {showToast && <ToastComponent show={showToast} setShow={setShowToast} text={toastText} />}
        </div>
    );
};

export default MenuItemsPage;
